import React from "react";
import { FilterButton } from "./FilterButton";
import { SelectMenu } from "../../../ui/SelectMenu";
import type { DatePreset } from "../hooks/useDateRangeFilter";
import { CalendarIcon } from "@heroicons/react/20/solid";

export interface DateRangeFilterProps {
  ref: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
  onToggle: () => void;
  selectedPreset: DatePreset;
  customStartDate: string;
  customEndDate: string;
  onPresetSelect: (preset: DatePreset) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onApply: () => void;
  onCancel: () => void;
  onRemove: () => void;
  isActive: boolean;
  canApply: boolean;
  currentLabel: string;
}

const presetOptions: Array<{ value: DatePreset; label: string }> = [
  { value: "last1day", label: "Last 1 day" },
  { value: "last7days", label: "Last 7 days" },
  { value: "last30days", label: "Last 30 days" },
  { value: "last90days", label: "Last 90 days" },
  { value: "lastYear", label: "Last year" },
  { value: "allTime", label: "All time" },
  // "Fixed range" names the value (two specific dates), not its provenance —
  // a saved baseline lands here too, and "Custom" would claim you typed it.
  { value: "custom", label: "Fixed range" },
];

/**
 * Filter component for selecting date range (presets or custom).
 */
export const DateRangeFilter = React.forwardRef<
  HTMLDivElement,
  Omit<DateRangeFilterProps, "ref">
>(
  (
    {
      isExpanded,
      onToggle,
      selectedPreset,
      customStartDate,
      customEndDate,
      onPresetSelect,
      onStartDateChange,
      onEndDateChange,
      onApply,
      onCancel,
      onRemove,
      isActive,
      canApply,
      currentLabel,
    },
    ref
  ) => {
    const isCustomRange = selectedPreset === "custom";
    const hasInvalidRange = isCustomRange && customStartDate && customEndDate && customStartDate > customEndDate;

    return (
      <div ref={ref} className="relative">
        <FilterButton
          label={`Date Range: ${currentLabel}`}
          isActive={isActive}
          isExpanded={isExpanded}
          onClick={onToggle}
          onRemove={isActive ? onRemove : undefined}
          icon={<CalendarIcon className="h-4 w-4" />}
        />

        {isExpanded && (
          <div className="absolute bg-popover border border-border rounded-lg shadow-md p-4 z-10 mt-1 min-w-[320px]">
            {/* Preset Options */}
            <div className="mb-4">
              <SelectMenu
                options={presetOptions}
                selectedValue={selectedPreset}
                onSelect={onPresetSelect}
                minWidth="100%"
                className="relative shadow-none border-0 p-0"
              />
            </div>

            {/* Custom Date Range Inputs */}
            {isCustomRange && (
              <div className="mb-4 space-y-3 border-t pt-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => onStartDateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    max={customEndDate || undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    min={customStartDate || undefined}
                  />
                </div>
                {hasInvalidRange && (
                  <p className="text-sm text-destructive">Start date must be before end date</p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            {isCustomRange && (
              <div className="flex gap-2 pt-3 border-t">
                <button
                  onClick={onCancel}
                  className="flex-1 px-3 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  Cancel
                </button>
                <button
                  onClick={onApply}
                  disabled={!canApply}
                  className="flex-1 px-3 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-muted disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

DateRangeFilter.displayName = "DateRangeFilter";
